import { useState } from "react";
import AppShell from "@/components/AppShell";
import TeacherClassPicker from "@/components/TeacherClassPicker";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { NotebookPen } from "lucide-react";
import {
  classKey,
  classLabelFromKey,
  getHomeworkForClass,
  getSessionUser,
  saveHomeworkForClass,
} from "@/lib/eisStore";
import { addHomework as addHomeworkToSupabase, decodeClassKey, isSupabaseConfigured } from "@/lib/supabaseOps";

function HomeworkFeed({ title, className, items }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle style={{ color: "#0b1f7a" }}>{title}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm text-slate-500">{className}</p>
        {items.length ? (
          items.map((item) => (
            <div key={item.id} className="rounded-xl border border-slate-200 p-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="font-semibold text-slate-800">{item.subject}</p>
                <span className="text-xs font-semibold px-2 py-1 rounded-full bg-slate-100 text-slate-600">
                  Due {item.dueDate}
                </span>
              </div>
              <p className="text-sm text-slate-600 mt-2 whitespace-pre-wrap">{item.homework}</p>
            </div>
          ))
        ) : (
          <div className="p-12 flex flex-col items-center justify-center text-center rounded-xl border-2 border-dashed border-slate-300">
            <div
              className="h-16 w-16 rounded-full flex items-center justify-center mb-4"
              style={{ backgroundColor: "#EEF0FA" }}
            >
              <NotebookPen size={28} style={{ color: "#0b1f7a" }} />
            </div>
            <p data-testid="homework-empty" className="text-lg font-semibold text-slate-700">
              no homework today
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function StudentHomework({ user }) {
  const studentClassKey = classKey(user.standard, user.division);
  const items = [...getHomeworkForClass(studentClassKey)].reverse();

  return (
    <AppShell>
      <div data-testid="homework-page" className="space-y-6">
        <h2 className="text-2xl md:text-3xl font-bold" style={{ color: "#0b1f7a" }}>
          Homework
        </h2>
        <HomeworkFeed
          title="Class Homework"
          className={classLabelFromKey(studentClassKey)}
          items={items}
        />
      </div>
    </AppShell>
  );
}

function TeacherHomework({ user }) {
  const [teacherClassKey, setTeacherClassKey] = useState(user.assignedClassKey || "");
  const [subject, setSubject] = useState("");
  const [homework, setHomework] = useState("");
  const [dueDate, setDueDate] = useState("");
  const items = teacherClassKey ? [...getHomeworkForClass(teacherClassKey)].reverse() : [];

  const submit = async (e) => {
    e.preventDefault();
    if (!teacherClassKey) {
      toast.error("Please select a class first");
      return;
    }
    if (!subject.trim() || !homework.trim() || !dueDate) {
      toast.error("Please fill in subject, homework, and due date");
      return;
    }

    if (isSupabaseConfigured) {
      try {
        const { standard, division } = decodeClassKey(teacherClassKey);
        await addHomeworkToSupabase({
          standard,
          division,
          subject: subject.trim(),
          description: homework.trim(),
          dueDate,
          file: null,
        });
      } catch (error) {
        console.error(error);
      }
    }

    saveHomeworkForClass(teacherClassKey, {
      subject: subject.trim(),
      homework: homework.trim(),
      description: homework.trim(),
      dueDate,
      teacherName: user.name,
    });
    setSubject("");
    setHomework("");
    setDueDate("");
    toast.success("Homework added");
  };

  return (
    <AppShell>
      <div data-testid="homework-page" className="space-y-6">
        <h2 className="text-2xl md:text-3xl font-bold" style={{ color: "#0b1f7a" }}>
          Homework
        </h2>

        <TeacherClassPicker teacher={user} onChange={setTeacherClassKey} />

        <div className="grid grid-cols-1 lg:grid-cols-[0.95fr_1.05fr] gap-6">
          <Card>
            <CardHeader>
              <CardTitle style={{ color: "#0b1f7a" }}>Add Homework</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={submit} className="space-y-4">
                <div>
                  <Label htmlFor="homework-subject">Subject</Label>
                  <Input
                    id="homework-subject"
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    className="mt-1"
                    placeholder="Mathematics"
                  />
                </div>
                <div>
                  <Label htmlFor="homework-body">Homework</Label>
                  <Textarea
                    id="homework-body"
                    value={homework}
                    onChange={(e) => setHomework(e.target.value)}
                    className="mt-1 min-h-32"
                    placeholder="Write the assignment instructions"
                  />
                </div>
                <div>
                  <Label htmlFor="homework-due-date">Due Date</Label>
                  <Input
                    id="homework-due-date"
                    type="date"
                    value={dueDate}
                    onChange={(e) => setDueDate(e.target.value)}
                    className="mt-1"
                  />
                </div>
                <Button
                  type="submit"
                  className="rounded-full text-white"
                  style={{ backgroundColor: "#0b1f7a" }}
                >
                  Publish Homework
                </Button>
              </form>
            </CardContent>
          </Card>

          <HomeworkFeed
            title="Published Homework"
            className={teacherClassKey ? classLabelFromKey(teacherClassKey) : "Select a class first"}
            items={items}
          />
        </div>
      </div>
    </AppShell>
  );
}

export default function Homework() {
  const user = getSessionUser();
  if (user?.role === "teacher") {
    return <TeacherHomework user={user} />;
  }
  return <StudentHomework user={user || {}} />;
}
